import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, Building2, Home, ImagePlus, Loader2, X } from "lucide-react";

const STEPS = [
  "Type de bien",
  "Adresse",
  "Surface & pièces",
  "Étage & DPE",
  "Votre projet",
  "Photos (optionnel)",
  "Vos coordonnées",
];

const DPE_COLORS = {
  A: "border-emerald-400/70 text-emerald-300",
  B: "border-lime-400/70 text-lime-300",
  C: "border-yellow-300/70 text-yellow-200",
  D: "border-amber-400/70 text-amber-300",
  E: "border-orange-400/70 text-orange-300",
  F: "border-orange-600/80 text-orange-400",
  G: "border-red-500/80 text-red-400",
};

const ETATS = ["Rénové récemment", "Bon état", "À rafraîchir", "À rénover"];
const DELAIS = ["Immédiat", "Moins de 3 mois", "3 à 6 mois", "6 à 12 mois", "Je me renseigne"];
const EXTERIEURS = [
  { value: "aucun", label: "Aucun" },
  { value: "balcon", label: "Balcon" },
  { value: "terrasse", label: "Terrasse" },
  { value: "jardin", label: "Jardin" },
];
const MAX_PHOTOS = 5;
const MAX_PHOTO_BYTES = 8 * 1024 * 1024;

const initialData = {
  type_bien: "",
  rue: "",
  code_postal: "",
  ville: "",
  surface: "",
  surface_carrez: "",
  exterieur: "",
  pieces: "",
  chambres: "",
  etage: "",
  ascenseur: null,
  dpe: "",
  etat: "",
  delai_projet: "",
  photos: [],
  prenom: "",
  nom: "",
  telephone: "",
  email: "",
  consent_rgpd: false,
  consent_recontact: false,
};

function Option({ selected, onClick, testid, children, className = "" }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`border px-5 py-4 text-left text-sm transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 ${
        selected
          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-amber-100"
          : "border-stone-700 bg-[#141417] text-stone-300 hover:border-[#D4AF37]/50"
      } ${className}`}
    >
      {children}
    </button>
  );
}

function Field({ label, error, testid, children }) {
  return (
    <div>
      <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-400">{label}</label>
      {children}
      {error && (
        <p data-testid={`${testid}-error`} className="mt-2 text-xs text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

export default function EstimationWizard() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const set = (key, value) => {
    setData((d) => ({ ...d, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList).slice(0, MAX_PHOTOS - data.photos.length);
    for (const f of files) {
      if (f.size > MAX_PHOTO_BYTES) {
        toast.error(`${f.name} dépasse 8 Mo.`);
        continue;
      }
      const preview = URL.createObjectURL(f);
      setData((d) => ({ ...d, photos: [...d.photos, { path: null, preview, name: f.name, uploading: true }] }));
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setData((d) => ({
          ...d,
          photos: d.photos.map((p) => (p.preview === preview ? { ...p, path: res.data.path, uploading: false } : p)),
        }));
      } catch {
        setData((d) => ({ ...d, photos: d.photos.filter((p) => p.preview !== preview) }));
        toast.error(`Échec de l'envoi de ${f.name}. Réessayez.`);
      }
    }
  };

  const removePhoto = (preview) => {
    URL.revokeObjectURL(preview);
    setData((d) => ({ ...d, photos: d.photos.filter((p) => p.preview !== preview) }));
  };

  const validate = () => {
    const e = {};
    if (step === 0 && !data.type_bien) e.type_bien = "Choisissez le type de votre bien.";
    if (step === 1) {
      if (!data.rue.trim()) e.rue = "Indiquez la rue du bien.";
      if (!/^\d{5}$/.test(data.code_postal.trim())) e.code_postal = "Code postal à 5 chiffres requis.";
      if (!data.ville.trim()) e.ville = "Indiquez la ville.";
    }
    if (step === 2) {
      if (!data.surface || Number(data.surface) <= 0) e.surface = "Surface habitable totale requise.";
      if (!data.pieces) e.pieces = "Sélectionnez le nombre de pièces.";
      if (data.surface_carrez !== "") {
        const sc = Number(data.surface_carrez);
        if (!(sc > 0)) e.surface_carrez = "Surface Carrez invalide.";
        else if (Number(data.surface) > 0 && sc > Number(data.surface) * 1.05)
          e.surface_carrez = "La surface Carrez dépasse rarement la surface totale — vérifiez.";
      }
    }
    if (step === 3) {
      if (data.type_bien === "appartement" && data.etage === "") e.etage = "Sélectionnez l'étage.";
      if (data.type_bien === "appartement" && Number(data.etage) >= 2 && data.ascenseur === null)
        e.ascenseur = "Ascenseur : oui ou non ?";
      if (!data.dpe) e.dpe = "Sélectionnez le DPE du bien.";
      if (!data.etat) e.etat = "Sélectionnez l'état général.";
      if (!data.exterieur) e.exterieur = "Précisez l'extérieur (ou « Aucun »).";
    }
    if (step === 4 && !data.delai_projet) e.delai_projet = "Précisez votre calendrier.";
    if (step === 6) {
      if (!data.prenom.trim()) e.prenom = "Prénom requis.";
      if (!data.nom.trim()) e.nom = "Nom requis.";
      if (!/^[\d\s+.-]{8,}$/.test(data.telephone.trim())) e.telephone = "Numéro de téléphone valide requis.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) e.email = "Adresse email valide requise.";
      if (!data.consent_rgpd) e.consent_rgpd = "Consentement obligatoire pour établir l'estimation.";
      if (!data.consent_recontact) e.consent_recontact = "Votre accord est nécessaire pour être recontacté(e).";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (!validate()) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        lead: {
          prenom: data.prenom.trim(),
          nom: data.nom.trim(),
          telephone: data.telephone.trim(),
          email: data.email.trim(),
          consent_rgpd: data.consent_rgpd,
          consent_recontact: data.consent_recontact,
        },
        property: {
          type_bien: data.type_bien,
          rue: data.rue.trim(),
          code_postal: data.code_postal.trim(),
          ville: data.ville.trim(),
          surface: Number(data.surface),
          surface_carrez: data.surface_carrez === "" ? null : Number(data.surface_carrez),
          exterieur: data.exterieur || null,
          pieces: Number(data.pieces),
          chambres: data.chambres === "" ? null : Number(data.chambres),
          etage: data.type_bien === "appartement" ? Number(data.etage) : null,
          ascenseur: data.type_bien === "appartement" ? data.ascenseur : null,
          dpe: data.dpe,
          etat: data.etat,
          delai_projet: data.delai_projet,
        },
        photos: data.photos.filter((p) => p.path).map((p) => p.path),
      };
      const res = await api.post("/estimations", payload);
      toast.success("Votre estimation est prête.");
      navigate(`/rapport/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Une erreur est survenue. Réessayez.");
      setSubmitting(false);
    }
  };

  const stepVariants = {
    enter: { opacity: 0, x: 48 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -48 },
  };

  return (
    <div data-testid="estimation-wizard" className="min-h-screen bg-[#0B0B0D] text-[#F5F3EF]">
      <header className="border-b border-[#D4AF37]/10 bg-[#0B0B0D]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="wizard-brand-link" className="font-display text-xl text-[#F5F3EF]">
            Valeur <span className="italic text-[#D4AF37]">Prestige</span>
          </Link>
          <span data-testid="wizard-step-indicator" className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone-400">
            Étape {step + 1} / {STEPS.length} — {STEPS[step]}
          </span>
        </div>
        <div className="h-px bg-stone-800">
          <motion.div
            className="h-px bg-[#D4AF37]"
            animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-14 lg:py-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {step === 0 && (
              <div data-testid="wizard-step-type">
                <h1 className="font-display text-3xl font-light text-amber-100 sm:text-4xl">
                  Quel bien souhaitez-vous <em className="text-[#D4AF37]">estimer ?</em>
                </h1>
                <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Option
                    testid="option-type-appartement"
                    selected={data.type_bien === "appartement"}
                    onClick={() => set("type_bien", "appartement")}
                    className="flex items-center gap-4 !p-6"
                  >
                    <Building2 size={22} className="text-[#D4AF37]" strokeWidth={1.5} />
                    <span>
                      <span className="font-display block text-xl">Appartement</span>
                      <span className="text-xs text-stone-500">Haussmannien, loft, duplex…</span>
                    </span>
                  </Option>
                  <Option
                    testid="option-type-maison"
                    selected={data.type_bien === "maison"}
                    onClick={() => set("type_bien", "maison")}
                    className="flex items-center gap-4 !p-6"
                  >
                    <Home size={22} className="text-[#D4AF37]" strokeWidth={1.5} />
                    <span>
                      <span className="font-display block text-xl">Maison</span>
                      <span className="text-xs text-stone-500">Hôtel particulier, villa…</span>
                    </span>
                  </Option>
                </div>
                {errors.type_bien && (
                  <p data-testid="type-bien-error" className="mt-4 text-xs text-red-400">{errors.type_bien}</p>
                )}
              </div>
            )}

            {step === 1 && (
              <div data-testid="wizard-step-adresse">
                <h1 className="font-display text-3xl font-light text-amber-100 sm:text-4xl">
                  Où se situe <em className="text-[#D4AF37]">votre bien ?</em>
                </h1>
                <p className="mt-4 text-sm text-stone-400">
                  Paris et toute l'Île-de-France — du studio haussmannien à l'hôtel particulier.
                </p>
                <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field label="Rue" error={errors.rue} testid="input-rue">
                    <input
                      data-testid="input-rue"
                      className="input-dark sm:col-span-2"
                      placeholder="Ex : 12 rue Lepic"
                      value={data.rue}
                      onChange={(e) => set("rue", e.target.value)}
                    />
                  </Field>
                  <Field label="Code postal" error={errors.code_postal} testid="input-code-postal">
                    <input
                      data-testid="input-code-postal"
                      className="input-dark"
                      placeholder="75018"
                      inputMode="numeric"
                      maxLength={5}
                      value={data.code_postal}
                      onChange={(e) => set("code_postal", e.target.value.replace(/\D/g, ""))}
                    />
                  </Field>
                  <Field label="Ville" error={errors.ville} testid="input-ville">
                    <input
                      data-testid="input-ville"
                      className="input-dark"
                      placeholder="Paris"
                      value={data.ville}
                      onChange={(e) => set("ville", e.target.value)}
                    />
                  </Field>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="wizard-step-surface">
                <h1 className="font-display text-3xl font-light text-amber-100 sm:text-4xl">
                  Parlez-moi de <em className="text-[#D4AF37]">sa surface.</em>
                </h1>
                <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-3">
                  <Field label="Surface habitable totale (m²)" error={errors.surface} testid="input-surface">
                    <input
                      data-testid="input-surface"
                      className="input-dark"
                      placeholder="85"
                      inputMode="decimal"
                      value={data.surface}
                      onChange={(e) => set("surface", e.target.value.replace(/[^\d.,]/g, ""))}
                    />
                  </Field>
                  {data.type_bien === "appartement" && (
                    <Field label="Surface loi Carrez (optionnel)" error={errors.surface_carrez} testid="input-carrez">
                      <input
                        data-testid="input-carrez"
                        className="input-dark"
                        placeholder="82 — si différente"
                        inputMode="decimal"
                        value={data.surface_carrez}
                        onChange={(e) => set("surface_carrez", e.target.value.replace(/[^\d.,]/g, ""))}
                      />
                    </Field>
                  )}
                  <Field label="Nombre de pièces" error={errors.pieces} testid="select-pieces">
                    <div className="flex flex-wrap gap-2">
                      {[1, 2, 3, 4, 5, "6+"].map((p) => (
                        <Option
                          key={p}
                          testid={`option-pieces-${p}`}
                          selected={String(data.pieces) === String(p === "6+" ? 6 : p)}
                          onClick={() => set("pieces", p === "6+" ? 6 : p)}
                          className="!px-4 !py-3"
                        >
                          {p}
                        </Option>
                      ))}
                    </div>
                  </Field>
                  <Field label="Chambres (optionnel)" testid="select-chambres">
                    <div className="flex flex-wrap gap-2">
                      {[1, 2, 3, 4, "5+"].map((c) => (
                        <Option
                          key={c}
                          testid={`option-chambres-${c}`}
                          selected={String(data.chambres) === String(c === "5+" ? 5 : c)}
                          onClick={() => set("chambres", c === "5+" ? 5 : c)}
                          className="!px-4 !py-3"
                        >
                          {c}
                        </Option>
                      ))}
                    </div>
                  </Field>
                </div>
                {data.type_bien === "appartement" && (
                  <p className="mt-4 text-xs text-stone-600">
                    En copropriété, c'est la surface loi Carrez qui fait foi pour la vente — indiquez-la si vous la connaissez, sinon nous l'utiliserons égale à la surface totale.
                  </p>
                )}
              </div>
            )}

            {step === 3 && (
              <div data-testid="wizard-step-dpe">
                <h1 className="font-display text-3xl font-light text-amber-100 sm:text-4xl">
                  Étage, état & <em className="text-[#D4AF37]">performance énergétique.</em>
                </h1>
                <div className="mt-10 space-y-10">
                  {data.type_bien === "appartement" && (
                    <>
                      <Field label="Étage" error={errors.etage} testid="select-etage">
                        <div className="flex flex-wrap gap-2">
                          {["RDC", 1, 2, 3, 4, 5, "6+"].map((f) => {
                            const val = f === "RDC" ? 0 : f === "6+" ? 6 : f;
                            return (
                              <Option
                                key={f}
                                testid={`option-etage-${f}`}
                                selected={String(data.etage) === String(val)}
                                onClick={() => {
                                  set("etage", val);
                                  if (val < 2) set("ascenseur", null);
                                }}
                                className="!px-4 !py-3"
                              >
                                {f}
                              </Option>
                            );
                          })}
                        </div>
                      </Field>
                      {Number(data.etage) >= 2 && (
                        <Field label="Ascenseur" error={errors.ascenseur} testid="select-ascenseur">
                          <div className="flex gap-2">
                            <Option
                              testid="option-ascenseur-oui"
                              selected={data.ascenseur === true}
                              onClick={() => set("ascenseur", true)}
                              className="!px-6 !py-3"
                            >
                              Oui
                            </Option>
                            <Option
                              testid="option-ascenseur-non"
                              selected={data.ascenseur === false}
                              onClick={() => set("ascenseur", false)}
                              className="!px-6 !py-3"
                            >
                              Non
                            </Option>
                          </div>
                        </Field>
                      )}
                    </>
                  )}
                  <Field label="Extérieur" error={errors.exterieur} testid="select-exterieur">
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {EXTERIEURS.map((x) => (
                        <Option
                          key={x.value}
                          testid={`option-exterieur-${x.value}`}
                          selected={data.exterieur === x.value}
                          onClick={() => set("exterieur", x.value)}
                          className="!px-4 !py-3 text-center"
                        >
                          {x.label}
                        </Option>
                      ))}
                    </div>
                  </Field>
                  <Field label="Diagnostic de Performance Énergétique (DPE)" error={errors.dpe} testid="select-dpe">
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(DPE_COLORS).map((letter) => (
                        <button
                          key={letter}
                          type="button"
                          data-testid={`option-dpe-${letter}`}
                          onClick={() => set("dpe", letter)}
                          className={`h-12 w-12 border font-mono text-sm transition-[transform,background-color] duration-300 hover:-translate-y-0.5 ${DPE_COLORS[letter]} ${
                            data.dpe === letter ? "bg-white/10 ring-1 ring-[#D4AF37]" : "bg-[#141417]"
                          }`}
                        >
                          {letter}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="État général du bien" error={errors.etat} testid="select-etat">
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {ETATS.map((etat) => (
                        <Option
                          key={etat}
                          testid={`option-etat-${etat.toLowerCase().replace(/[^a-z]/g, "-")}`}
                          selected={data.etat === etat}
                          onClick={() => set("etat", etat)}
                          className="!px-4 !py-3 text-center"
                        >
                          {etat}
                        </Option>
                      ))}
                    </div>
                  </Field>
                </div>
              </div>
            )}

            {step === 4 && (
              <div data-testid="wizard-step-projet">
                <h1 className="font-display text-3xl font-light text-amber-100 sm:text-4xl">
                  Quel est <em className="text-[#D4AF37]">votre calendrier ?</em>
                </h1>
                <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {DELAIS.map((d) => (
                    <Option
                      key={d}
                      testid={`option-delai-${d.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      selected={data.delai_projet === d}
                      onClick={() => set("delai_projet", d)}
                      className="!p-5"
                    >
                      <span className="font-display text-lg">{d}</span>
                    </Option>
                  ))}
                </div>
                {errors.delai_projet && (
                  <p data-testid="delai-projet-error" className="mt-4 text-xs text-red-400">{errors.delai_projet}</p>
                )}
              </div>
            )}

            {step === 5 && (
              <div data-testid="wizard-step-photos">
                <h1 className="font-display text-3xl font-light text-amber-100 sm:text-4xl">
                  Quelques photos <em className="text-[#D4AF37]">de votre bien ?</em>
                </h1>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-stone-400">
                  Optionnel, mais précieux : elles permettent à votre conseiller d'affiner l'estimation
                  avant même la visite. Vues, volumes, lumière — montrez l'essentiel. {MAX_PHOTOS} photos maximum, 8 Mo chacune.
                </p>
                <div className="mt-10">
                  <label
                    data-testid="photo-upload-zone"
                    className="flex cursor-pointer flex-col items-center justify-center gap-3 border border-dashed border-stone-700 bg-[#141417] px-6 py-12 text-center transition-[border-color,background-color] duration-300 hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/5"
                  >
                    <ImagePlus size={26} className="text-[#D4AF37]" strokeWidth={1.5} />
                    <span className="text-sm text-stone-300">
                      {data.photos.length >= MAX_PHOTOS
                        ? "Nombre maximum de photos atteint"
                        : "Cliquez pour ajouter des photos (JPG, PNG, WebP)"}
                    </span>
                    <input
                      type="file"
                      data-testid="photo-upload-input"
                      accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                      multiple
                      className="hidden"
                      disabled={data.photos.length >= MAX_PHOTOS}
                      onChange={(e) => {
                        handleFiles(e.target.files);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {data.photos.length > 0 && (
                    <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="photo-preview-grid">
                      {data.photos.map((p, i) => (
                        <div key={p.preview} data-testid={`photo-preview-${i}`} className="group relative border border-stone-800">
                          <img src={p.preview} alt={p.name} className="h-24 w-full object-cover" />
                          {p.uploading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                              <Loader2 size={18} className="animate-spin text-[#D4AF37]" />
                            </div>
                          )}
                          <button
                            type="button"
                            data-testid={`photo-remove-${i}`}
                            onClick={() => removePhoto(p.preview)}
                            className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-stone-600 bg-[#0B0B0D] text-stone-400 transition-colors hover:border-red-400 hover:text-red-400"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="mt-4 text-xs text-stone-600">
                    Vos photos ne sont visibles que par votre conseiller — jamais publiées.
                  </p>
                </div>
              </div>
            )}

            {step === 6 && (
              <div data-testid="wizard-step-coordonnees">
                <h1 className="font-display text-3xl font-light text-amber-100 sm:text-4xl">
                  Où vous envoyer <em className="text-[#D4AF37]">votre estimation ?</em>
                </h1>
                <p className="mt-4 text-sm text-stone-400">
                  Votre rapport s'affiche immédiatement, vous est envoyé par email et reste téléchargeable.
                  Vos coordonnées permettent à votre conseiller de l'affiner avec vous.
                </p>
                <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field label="Prénom" error={errors.prenom} testid="input-prenom">
                    <input
                      data-testid="input-prenom"
                      className="input-dark"
                      placeholder="Camille"
                      value={data.prenom}
                      onChange={(e) => set("prenom", e.target.value)}
                    />
                  </Field>
                  <Field label="Nom" error={errors.nom} testid="input-nom">
                    <input
                      data-testid="input-nom"
                      className="input-dark"
                      placeholder="Durand"
                      value={data.nom}
                      onChange={(e) => set("nom", e.target.value)}
                    />
                  </Field>
                  <Field label="Téléphone" error={errors.telephone} testid="input-telephone">
                    <input
                      data-testid="input-telephone"
                      className="input-dark"
                      placeholder="06 12 34 56 78"
                      inputMode="tel"
                      value={data.telephone}
                      onChange={(e) => set("telephone", e.target.value)}
                    />
                  </Field>
                  <Field label="Email" error={errors.email} testid="input-email">
                    <input
                      data-testid="input-email"
                      className="input-dark"
                      placeholder="camille@exemple.fr"
                      inputMode="email"
                      value={data.email}
                      onChange={(e) => set("email", e.target.value)}
                    />
                  </Field>
                </div>
                <div className="mt-8 space-y-4">
                  <label className="flex cursor-pointer items-start gap-3 text-sm text-stone-300" data-testid="consent-rgpd-label">
                    <input
                      type="checkbox"
                      data-testid="checkbox-consent-rgpd"
                      checked={data.consent_rgpd}
                      onChange={(e) => set("consent_rgpd", e.target.checked)}
                      className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]"
                    />
                    <span>
                      J'accepte que mes données soient utilisées pour établir mon estimation et me la transmettre (RGPD).{" "}
                      <span className="text-[#D4AF37]">*</span>
                    </span>
                  </label>
                  {errors.consent_rgpd && (
                    <p data-testid="consent-rgpd-error" className="text-xs text-red-400">{errors.consent_rgpd}</p>
                  )}
                  <label className="flex cursor-pointer items-start gap-3 text-sm text-stone-300" data-testid="consent-recontact-label">
                    <input
                      type="checkbox"
                      data-testid="checkbox-consent-recontact"
                      checked={data.consent_recontact}
                      onChange={(e) => set("consent_recontact", e.target.checked)}
                      className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37]"
                    />
                    <span>
                      J'autorise Barsamia Tumba à me recontacter au sujet de mon projet de vente.{" "}
                      <span className="text-[#D4AF37]">*</span>
                    </span>
                  </label>
                  {errors.consent_recontact && (
                    <p data-testid="consent-recontact-error" className="text-xs text-red-400">{errors.consent_recontact}</p>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-14 flex items-center justify-between border-t border-stone-800 pt-8">
          <button
            type="button"
            data-testid="wizard-back-button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="btn-ghost !border-transparent !px-4 disabled:pointer-events-none disabled:opacity-0"
          >
            <ArrowLeft size={16} /> Retour
          </button>
          {step < STEPS.length - 1 ? (
            <button type="button" data-testid="wizard-next-button" onClick={next} className="btn-gold">
              Continuer <ArrowRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              data-testid="wizard-submit-button"
              onClick={submit}
              disabled={submitting}
              className="btn-gold disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Calcul en cours…
                </>
              ) : (
                <>
                  Recevoir mon estimation <ArrowRight size={16} />
                </>
              )}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
